import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ADMIN_ACCOUNTS, ADMIN_CRM, ADMIN_EVENTS, ADMIN_INVESTORS,
  ADMIN_INVITATIONS, ADMIN_PROJECTS, ADMIN_DOCS_REQUEST_TEMPLATE, DASHBOARD,
  ADMIN_DOCS_GENERAL,
  ADMIN_PROCESSES,
} from 'constants/routes';
import {
  Button, ButtonGroup, Col, Container, Nav, Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Storage from 'utils/Storage';
import { updateUserAccount, updateUserRole, updateUserToken } from 'store/User';
import handlePromise from 'utils/Promise';
import AuthService from 'services/api/Auth';
import ProfileService from 'services/api/Profile';
import { useDispatch } from 'react-redux';
import InvestmentAccountService from 'services/api/InvestmentAccount';
import { ModalErrorContext } from 'context/ModalError';
import { LoadingContext } from 'context/Loading';
import classNames from 'classnames';
import ActiveAccountModal from 'components/Modal/ActiveAccountModal';
import { MODAL_ACTIVE_ACCOUNT, MODAL_DISABLED_ACCOUNT } from 'constants/utils';
import { Account } from 'types/Account';
import { hasInvestorUser } from 'utils/investorContactGuards';

function TabsHeader( {
  title, type, id, credentialState, ssoUserId, roles, userId, extraData = null,
}:{
  type?:string;
  title?:string;
  id?:number;
  credentialState?:number;
  ssoUserId?: string | null;
  roles?: string[] | null;
  userId?: number | null;
  extraData?: any
} ) {
  const { toggleLoading } = useContext( LoadingContext );
  const { openErrorModal } = useContext( ModalErrorContext );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [showActiveAccountModal, setShowActiveAccountModal] = useState( false );
  const [fullAccount, setFullaccount] = useState<Account | undefined>( );
  const acccountAction = useRef<typeof MODAL_ACTIVE_ACCOUNT
  | typeof MODAL_DISABLED_ACCOUNT >( MODAL_ACTIVE_ACCOUNT );

  const closeActiveAccountModal = useCallback( ( ) => {
    setShowActiveAccountModal( false );
  }, [] );
  const [dataReload, setDataReloaded] = useState( new Date().valueOf() );

  const investorUserState = {
    fullName: title,
    credentialState,
    ssoUserId,
    roles,
    userId,
    extraData,
  };

  const canImpersonateInvestor = !!extraData?.email
    && hasInvestorUser( { credentialState, ssoUserId } );
  const showInvestorImpersonateBtn = type === 'user' && canImpersonateInvestor
    && ( location.pathname === `/admin/investors/${id}/accounts/user`
      || location.pathname === `/admin/investors/${id}/projects/user` );

  const openCreateUserModal = useCallback( (
    action: typeof MODAL_ACTIVE_ACCOUNT | typeof MODAL_DISABLED_ACCOUNT,
  ) => {
    acccountAction.current = action;
    setShowActiveAccountModal( ( state ) => !state );
  }, [] );

  const selectAccount = useCallback( ( accountId: string ) => {
    dispatch( updateUserAccount( accountId ) );
  }, [] );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getAccount = useCallback( async () => {
    toggleLoading( true );

    const [accountError, accountsResponse, accountsData] = await handlePromise(
      InvestmentAccountService.getInvestmentGetPersonal( Number( id ) ),
    );

    if ( accountsResponse.ok ) {
      setFullaccount( accountsData as Account );
    } else {
      const errMsg = accountError && typeof accountError === 'object' && 'err' in accountError
        ? String( ( accountError as { err: unknown } ).err )
        : String( accountError ?? 'Error' );
      openErrorModal( errMsg );
    }
    toggleLoading( false );
  }, [id] );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const changeToUser = useCallback( async () => {
    if ( !( extraData && extraData.email ) ) {
      return false;
    }
    toggleLoading( true );
    const [userTokenError, userTokenResponse, userTokenData] = await handlePromise(
      AuthService.getTokenImpersonate( {
        email: extraData.email,
      } ),
    );

    if ( userTokenResponse.ok ) {
      Storage.clear( 'grupoimpar:token' );
      Storage.set( 'grupoimpar:token', userTokenData.token );
      dispatch( updateUserToken( userTokenData.token ) );

      const [, profileResponse, profileData] = await handlePromise( ProfileService.getProfile() );
      if ( profileData?.isNew ) {
        toggleLoading( false );
        navigate( '/force-change-password' );
        return;
      }
      const [, accountsResponse, accountsData] = await handlePromise(
        InvestmentAccountService.getInvestmentAccounts(),
      );
      if ( profileResponse.ok ) {
        dispatch( updateUserRole( profileData?.role ) );
        if ( accountsResponse.ok && accountsData.length ) {
          if ( profileData?.role === 'Admin' && !accountsData?.length ) {
            selectAccount( 'admin' );
            navigate( ADMIN_ACCOUNTS );
          } else if ( accountsData?.length > 0 ) {
            selectAccount( accountsData[0].cuentaDeInversionId );
            navigate( DASHBOARD );
          }
        }
      }
      toggleLoading( false );
    } else {
      openErrorModal( userTokenError.toString() );
    }
  }, [] );

  useEffect( () => {
    if ( type === 'inv' ) {
      getAccount();
    }
  }, [type, dataReload] );

  return (
    <div className="nav__subheader nav__subheader-admin">
      <Container className="p-0">
        {!!type && (
        <>
          <div className="pt-1 pb-0">
            <Button variant="link" size="sm" className="text--semibold p-0" onClick={() => navigate( -1 )}>
              <i className="icon-arrow-left icon--xss" />
              Volver
            </Button>
          </div>
          <Row className="my-3 align-items-center">
            <Col md>
              <h4 className="text--xlg text--medium text-primary">{title}</h4>
            </Col>
            {type === 'inv' && (
              <Col md="auto">
                <ButtonGroup className="btn-group-switch">
                  <Button
                    variant="link"
                    size="sm"
                    className={classNames( { active: fullAccount?.activo === true } )}
                    onClick={() => {
                      if ( fullAccount?.activo === false ) {
                        openCreateUserModal( MODAL_ACTIVE_ACCOUNT );
                      }
                    }}
                  >
                    Activado
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className={classNames( { active: fullAccount?.activo === false } )}
                    onClick={() => {
                      if ( fullAccount?.activo === true ) {
                        openCreateUserModal( MODAL_DISABLED_ACCOUNT );
                      }
                    }}
                  >
                    Desactivado
                  </Button>
                </ButtonGroup>
              </Col>
            )}
          </Row>
        </>
        )}

        <Row>
          <Col xs={12}>
            <Nav variant="tabs" className={classNames( 'w-100', { 'border-bottom ps-4': !!type } )}>
              {type === 'inv' && (
                <>
                  <Nav.Item>
                    <Nav.Link
                      onClick={() => { navigate( `/admin/accounts/${id}/docs/${type}`, { state: { nameCuentadeInversion: title }, replace: true } ); }}
                      eventKey="accounts-docs"
                      disabled={location.pathname === `/admin/accounts/${id}/docs/inv`}
                      active={location.pathname === `/admin/accounts/${id}/docs/inv`}
                    >
                      Documentación
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      onClick={() => { navigate( `/admin/accounts/${id}/projects/${type}`, { state: { nameCuentadeInversion: title }, replace: true } ); }}
                      eventKey="accounts-inv"
                      disabled={location.pathname === `/admin/accounts/${id}/projects/inv`}
                      active={location.pathname === `/admin/accounts/${id}/projects/inv`}
                    >
                      Promociones
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      onClick={() => { navigate( `/admin/accounts/${id}/investors/${type}`, { state: { nameCuentadeInversion: title }, replace: true } ); }}
                      eventKey="accounts-inv"
                      disabled={location.pathname === `/admin/accounts/${id}/investors/inv`}
                      active={location.pathname === `/admin/accounts/${id}/investors/inv`}
                    >
                      Contactos asociados
                    </Nav.Link>
                  </Nav.Item>
                </>
              )}
              {type === 'pro' && (
                <>
                  <Nav.Item>
                    <Nav.Link
                      onClick={() => { navigate( `/admin/projects/${id}/accounts/${type}`, { state: { codigoPromocion: title }, replace: true } ); }}
                      eventKey="projects-inv"
                      disabled={location.pathname === `/admin/projects/${id}/accounts/pro`}
                      active={location.pathname === `/admin/projects/${id}/accounts/pro`}
                    >
                      Cuentas de inversión
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      onClick={() => { navigate( `/admin/projects/${id}/investors/${type}`, { state: { codigoPromocion: title }, replace: true } ); }}
                      eventKey="projects-inv"
                      disabled={location.pathname === `/admin/projects/${id}/investors/pro`}
                      active={location.pathname === `/admin/projects/${id}/investors/pro`}
                    >
                      Inversores
                    </Nav.Link>
                  </Nav.Item>
                </>
              )}
              {type === 'user' && (
                <>
                  <Nav.Item>
                    <Nav.Link
                      onClick={() => { navigate( `/admin/investors/${id}/accounts/${type}`, { state: investorUserState, replace: true } ); }}
                      eventKey="investors-inv"
                      disabled={location.pathname === `/admin/investors/${id}/accounts/user`}
                      active={location.pathname === `/admin/investors/${id}/accounts/user`}
                    >
                      Cuentas de inversión
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      onClick={() => { navigate( `/admin/investors/${id}/projects/${type}`, { state: investorUserState, replace: true } ); }}
                      eventKey="investors-inv"
                      disabled={location.pathname === `/admin/investors/${id}/projects/user`}
                      active={location.pathname === `/admin/investors/${id}/projects/user`}
                    >
                      Proyectos invertidos
                    </Nav.Link>
                  </Nav.Item>
                </>
              )}
              {!type && (
                <>
                  <Nav.Item>
                    <Nav.Link
                      as={NavLink}
                      to={ADMIN_ACCOUNTS}
                      end
                    >
                      Cuentas de inversión
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_INVESTORS}>
                      Inversores
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_PROJECTS}>Proyectos</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_INVITATIONS}>Invitaciones</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_EVENTS}>Eventos</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_DOCS_REQUEST_TEMPLATE}>
                      Plantillas solic. docs.
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_CRM}>CRM</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_DOCS_GENERAL}>Panel general docs</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as={NavLink} to={ADMIN_PROCESSES}>Procesos</Nav.Link>
                  </Nav.Item>
                </>
              )}
            </Nav>
          </Col>
          <Col md="auto" className="align-items-center d-flex">
            {showInvestorImpersonateBtn && (
            <Button
              className="pt-1 pb-1 ps-4 pe-4 text--normal btn--rounded d-flex align-items-center"
              onClick={() => changeToUser()}
            >
              {t( 'seeUser' )}
            </Button>
            )}
          </Col>
        </Row>
        {!!showActiveAccountModal && id && (
          <ActiveAccountModal
            show={showActiveAccountModal}
            onClose={closeActiveAccountModal}
            account={{
              id,
              fullName: title || '',
              action: acccountAction.current,
            }}
            reloadData={() => setDataReloaded( new Date().valueOf() )}
          />
        )}

      </Container>
    </div>
  );
}

export default TabsHeader;
